import {AuthModel} from './_models'
import {isStaff} from './staffAccess'

// Why the dashboard asks the user to set a password:
// - temporary-password: they signed in with a 48-hour password an administrator sent them (none is issued since
//   backend 1.0.0.757; the ones already sent run out);
// - forgot-password: they came through «رمز عبور را فراموش کرده‌اید؟» and signed in with an SMS code.
// An account without a username or password no longer gets a notice: «تکمیل حساب کاربری» asks for them before the
// panel opens (accountCompletion.ts).
export type CredentialNoticeReason = 'temporary-password' | 'forgot-password'

export const FORGOT_PASSWORD_QUERY = 'reason=forgot-password'

/** «امنیت و ورود», on the username and password card, asking for the new password. */
export const RESET_PASSWORD_PATH = '/profile/security?reset=1#profile-credentials'

const FORGOT_PASSWORD_KEY = 'mp:login-via-forgot-password'
const FORGOT_PASSWORD_REDIRECT_KEY = 'mp:forgot-password-redirect'
const DISMISSED_KEY = 'mp:credential-notice-dismissed'

// Session storage is a per-tab convenience here; a blocked or empty store only means the notice
// shows again, never that it is wrongly hidden for good.
const readSession = (key: string) => {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

const writeSession = (key: string, value?: string) => {
  try {
    if (value === undefined) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, value)
  } catch {
    // Storage unavailable (private mode, blocked site data): the notice simply is not remembered.
  }
}

export const isForgotPasswordLogin = (search: string) =>
  new URLSearchParams(search).get('reason') === 'forgot-password'

// Called after every successful sign-in, so the notice state always belongs to the latest login.
export const rememberLoginRoute = (viaForgotPassword: boolean) => {
  writeSession(FORGOT_PASSWORD_KEY, viaForgotPassword ? '1' : undefined)
  writeSession(FORGOT_PASSWORD_REDIRECT_KEY, viaForgotPassword ? '1' : undefined)
  writeSession(DISMISSED_KEY, undefined)
}

/**
 * Once, right after a «رمز عبور را فراموش کرده‌اید؟» sign-in: the page to go to instead of the usual landing — the
 * username and password card, where a fresh SMS sign-in sets the new password without other proof.
 */
export const takeForgotPasswordRedirect = (): string | undefined => {
  if (readSession(FORGOT_PASSWORD_REDIRECT_KEY) !== '1') return undefined
  writeSession(FORGOT_PASSWORD_REDIRECT_KEY, undefined)
  return RESET_PASSWORD_PATH
}

// Called once the user has saved a username and password of their own.
export const clearCredentialNotice = () => {
  writeSession(FORGOT_PASSWORD_KEY, undefined)
  writeSession(FORGOT_PASSWORD_REDIRECT_KEY, undefined)
  writeSession(DISMISSED_KEY, undefined)
}

export const dismissCredentialNotice = (reason: CredentialNoticeReason) =>
  writeSession(DISMISSED_KEY, reason)

export const resolveCredentialNotice = (
  user: AuthModel | undefined,
  viaForgotPassword = readSession(FORGOT_PASSWORD_KEY) === '1',
  dismissed = readSession(DISMISSED_KEY)
): CredentialNoticeReason | undefined => {
  // A support operator working inside a customer's panel is not the customer; staff have no credentials here.
  if (!user || user.support || isStaff(user)) return undefined

  let reason: CredentialNoticeReason | undefined
  if (user.temporaryPasswordInUse) reason = 'temporary-password'
  else if (viaForgotPassword) reason = 'forgot-password'

  return reason && reason !== dismissed ? reason : undefined
}
